import { useDispatch, useSelector } from "react-redux";
import { URLdecode, URLencode } from "../utils/url";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import { useRef } from "react";
import axios from "axios";
import fetchServer from "../controllers/fetchServer";
import { setPolicies } from "../redux/reducers/tour/insuranceSlice";
import { useInsuranceActions } from "./useInsuranceActions";
import { setLoadingModal } from "../redux/reducers/modalSlice";
import { configurations } from "../configurations";

function getInsuranceRequestBody(booking) {
  return (
    booking?.orderData?.orderData?.requestBody ||
    booking?.booking?.orderData?.orderData?.requestBody ||
    booking?.orderData?.requestBody ||
    null
  );
}

export function buildInsuranceRebookPayload(booking) {
  const requestBody = getInsuranceRequestBody(booking) || {};
  const orderData = booking?.orderData?.orderData || {};

  const payload = {
    personsInfo: requestBody.personsInfo || [],
    amount: requestBody.amount ?? booking?.grandTotal ?? orderData.amount,
    currency: requestBody.currency ?? booking?.currency ?? orderData.currency,
    originalAmount:
      requestBody.originalAmount ??
      booking?.originalAmount ??
      orderData.originalAmount,
    originalCurrency:
      requestBody.originalCurrency ??
      booking?.originalCurrency ??
      orderData.originalCurrency,
    policyId: requestBody.policyId ?? orderData.policyId,
    policyType: requestBody.policyType,
    address: requestBody.address,
    destinationCountry: requestBody.destinationCountry,
    flights: requestBody.flights || [],
    addonSelection: requestBody.addonSelection ?? null,
    supplier: requestBody.supplier ?? booking?.supplier ?? orderData.supplier,
  };

  if (requestBody.purposeOfTravel) {
    payload.purposeOfTravel = requestBody.purposeOfTravel;
  }

  return payload;
}

export function useInsuranceApi() {
  const params = URLdecode();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getActualMinAndMaxInsurancePrice, parseInsuranceBookingData } =
    useInsuranceActions();
  const { ticketType } = useSelector((state) => state.order);
  const cancelGetFlightPoliciesToken = useRef(null);
  const cancelOrdersToken = useRef(null);

  const parseFlightPolicyPayload = ({ data }) => {
    return {
      personInfo: {
        birthDate: data?.birthDate || "1990-10-20",
        firstName: data?.firstName || "yared",
        middleName: data?.middleName || "fire",
        lastName: data?.lastName || "terefe",
        email: data?.email || "admin-dembe@yopmail.com",
        phone: data?.phone || "08034635316",
        gender: data?.gender || "Male",
        title: data?.title || "Mr",
        city: data?.city || "Lagos",
        state: data?.state || "Lagos",
        passportNumber: data?.passportNumber || "18277872A4",
      },
      destinationCountry: data?.departure?.country,
      startDate: data?.startDate,
      endDate: data?.endDate,
      purposeOfTravel: data?.purposeOfTravel || "Leisure",
      isRoundTrip: data?.isRoundTrip || false,
      numberOfAdults: Number(data?.numberOfAdults) || 1,
      numberOfChildren: Number(data?.numberOfChildren) || 0,
      numberOfInfants: Number(data?.numberOfInfants) || 0,
      flights: data?.flights || [
        {
          departureDate: data?.startDate,
          arrivalDate: data?.endDate,
          flightNumber: data?.flightNumber || "2992AAD",
          pnr: data?.pnr || "FEDSAA",
          airline: data?.airline || "ET",
          departureAirport: data?.departure?.iata,
          arrivalAirport: data?.arrival?.iata,
          segmentIndex: 0,
        },
      ],
    };
  };

  const getFlightPolicies = async ({ data, setLoading, callback }) => {
    const payload = parseFlightPolicyPayload({ data });
    setLoading && setLoading(true);
    let cancellationMessage = "Operation canceled due to new request.";

    if (cancelGetFlightPoliciesToken.current) {
      cancelGetFlightPoliciesToken.current.cancel(cancellationMessage);
    }

    cancelGetFlightPoliciesToken.current = axios.CancelToken.source();

    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/flight-policies`,
      data: payload,
      cancelToken: cancelGetFlightPoliciesToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading && setLoading(false);
        if (res) {
          if (res.status === 200) {
            const policies = res?.data?.data?.data;
            if (callback) {
              callback(policies);
            } else {
              dispatch(setPolicies(policies));
              getActualMinAndMaxInsurancePrice(policies);
            }
          }
        }
      })
      .catch((err) => {});
  };

  const bookInsurance = async ({
    data,
    setLoading,
    insurance,
    callback,
    payload: customPayload,
  }) => {
    const payload =
      customPayload || parseInsuranceBookingData({ data, insurance });

    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/flight-purchase`,
      data: payload,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            enqueueSnackbar("Protection booking successful", {
              variant: "success",
            });
            if (callback) callback(res?.data?.data);
            else
              navigate(
                `/protection/payment?${URLencode({
                  id: res?.data?.data?.purchasedInsurance?._id,
                  policy: insurance?.policyId,
                })}`,
              );
          } else {
            enqueueSnackbar(
              `Protection booking failed: ${
                res.data.message || "Unknown error"
              }`,
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Insurance booking failed: Network error", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const initializeInsurancePayment = async ({
    setLoading,
    callback,
    booking,
  }) => {
    const data = {
      insurancePurchaseId: booking?._id,
      paymentMode: "Wallet",
      type: "admin",
    };
    setLoading && setLoading(true);

    await fetchServer({
      method: "POST",
      url: `/payment/v1/payment/insurance`,
      data,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data?.data;
            // callback(res);
            callback(bookingData || booking);
          } else {
            enqueueSnackbar(
              res?.data?.error ||
                res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const getInsuranceBooking = async (setReload) => {
    const params = URLdecode();

    dispatch(setLoadingModal(true));

    await fetchServer({
      method: "GET",
      url: `/product/v1/insurance/${params?.id}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const booking = res?.data?.data;
            let parameters = {
              id: booking?._id,
              status: booking?.status,
            };
            localStorage.setItem(
              `booking/${booking?._id}`,
              JSON.stringify(booking),
            );
            let url = `/protection/confirmation?${URLencode(parameters)}`;
            window.location.replace(url);
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
            setTimeout(() => {
              navigate(-1);
            }, 2000);
          }
        }
      })
      .catch((err) => {
        // console.log("Error: ", err);
        // enqueueSnackbar("Something went wrong. please try again later.", {
        //   variant: "error",
        // });
      })
      .finally(() => {
        setReload && setReload((prev) => !prev);
        dispatch(setLoadingModal(false));
      });
  };

  const cancelAndRefundInsurance = async ({
    booking,
    setLoading,
    callback,
    noSave = false,
  }) => {
    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/cancel-confirmed/${booking?._id}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data;
            enqueueSnackbar("Ticket cancelled successfully", {
              variant: "success",
            });
            if (callback) {
              callback(bookingData);
            }
            if (!noSave) {
              localStorage.setItem(
                `booking/${booking?._id}`,
                JSON.stringify(bookingData),
              );
            }
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const getInsuranceBookings = async (params, setLoading) => {
    setLoading?.(true);
    const active = ticketType === configurations.insuranceKeyword;
    let {
      limit = 0,
      offset = 0,
      searchBy,
      keyword,
      status,
      account,
    } = params || {};
    switch (status) {
      case "Paid":
        status = "Paid";
        break;
      case "Pending":
        status = "Booked,Pending";
        break;
      case "Canceled":
        status = "Canceled,Cancelled";
        break;
      case "Completed":
        status = "Issued";
        break;
      case "Refunded":
        status = "Refunded";
        break;
      default:
        status = "";
        break;
    }
    let cancellationMessage = "Operation canceled due to new request.";

    if (cancelOrdersToken.current) {
      cancelOrdersToken.current.cancel(cancellationMessage);
    }

    cancelOrdersToken.current = axios.CancelToken.source();

    const response = await fetchServer({
      method: "GET",
      url: `/product/v1/insurance?limit=${limit}&offset=${offset}${
        searchBy && active
          ? `&searchBy=contactFirstName,bookingId,supplier,contactEmail,requestBody.name&keyword=${keyword}`
          : ""
      }${status && active ? `&filterBy=status&filterValue=${status}` : ""}${
        account ? `&filterBy=account&filterValue=${account}` : ""
      }`,
      cancelToken: cancelOrdersToken.current.token,
    })
      .then((res) => {
        if (!res?.cancelled) setLoading?.(false);
        if (res) {
          if (res.status === 200) {
            return [
              Array.isArray(res.data.data?.data) ? res.data.data?.data : [],
              res?.data?.data?.meta?.total || 0,
            ];
          }
        }
      })
      .catch((err) => {});

    return response || [];
  };

  const getFlightOrderForInsurance = async (setLoading) => {
    const params = URLdecode();
    const savedBooking = localStorage.getItem(
      configurations.flightOrderSaveKey?.replace(":id", params?.flightId),
    );

    if (!params?.flightId) return;
    if (savedBooking) return;

    setLoading && setLoading(true);

    await fetchServer({
      method: "GET",
      url: `/product/v1/book/${params?.flightId}?populate=flightBooking`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const booking = res?.data?.data?.booking;
            localStorage.setItem(
              configurations.flightOrderSaveKey?.replace(":id", booking?._id),
              JSON.stringify(booking),
            );
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
            setTimeout(() => {
              navigate(-1);
            }, 2000);
          }
        }
      })
      .catch((err) => {})
      .finally(() => {
        setLoading && setLoading(false);
      });
  };

  const cancelPendingInsuranceOrder = async ({
    booking,
    setLoading,
    callbackUrl,
  }) => {
    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/cancel-purchase/${booking?._id}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data;
            callbackUrl && callbackUrl(bookingData);
            enqueueSnackbar("Booking cancelled successfully", {
              variant: "success",
            });
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const updateFarePrice = async ({
    booking,
    setLoading,
    callbackUrl,
    fare,
  }) => {
    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/update-fare/${booking?._id}`,
      data: {
        fare,
      },
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data;
            callbackUrl && callbackUrl(bookingData);
            enqueueSnackbar("Fare updated successfully", {
              variant: "success",
            });
          } else {
            // accommodationFareUpdateError({ data: res?.data });
            // enqueueSnackbar(
            //   res?.data?.message ||
            //     "Something went wrong. please try again later.",
            //   {
            //     variant: "error",
            //   }
            // );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const confirmInsuranceOrder = async ({
    booking,
    setLoading,
    callbackUrl,
  }) => {
    const insuranceBookingId = booking?._id || booking?.mainBooking?._id;
    // const insuranceBookingId = booking?.mainBooking?.insuranceBooking?.[0];

    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/payment/v1/payment/admin-confirm-insurance?bookingId=${insuranceBookingId}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data;
            callbackUrl && callbackUrl(bookingData);
            enqueueSnackbar("Insurance issued successfully", {
              variant: "success",
            });
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                res?.data?.error ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const declineAwaitingAdminInsuranceOrder = async ({
    booking,
    setLoading,
    callbackUrl,
  }) => {
    const insuranceBookingId = booking?._id || booking?.mainBooking?._id;

    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/payment/v1/payment/admin-decline-insurance?bookingId=${insuranceBookingId}`,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const responseData = res?.data?.data;
            const bookingData = responseData?.booking || responseData;
            callbackUrl && callbackUrl(bookingData, responseData);
            enqueueSnackbar("Insurance booking declined successfully", {
              variant: "success",
            });
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                res?.data?.error ||
                "Something went wrong. please try again later.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch((err) => {
        enqueueSnackbar("Something went wrong. please try again later.", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
  };

  const extractInsurancePolicies = (res) => {
    const data = res?.data?.data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    if (Array.isArray(res?.data)) return res.data;
    return [];
  };

  const searchInsurancePolicies = async ({ payload }) => {
    try {
      const res = await fetchServer({
        method: "POST",
        url: `/product/v1/insurance/flight-policies`,
        data: payload,
      });

      if (res?.status === 200) {
        return extractInsurancePolicies(res);
      }

      enqueueSnackbar(
        res?.data?.message ||
          res?.message ||
          "Could not refresh insurance policies.",
        { variant: "error" },
      );
      return null;
    } catch (err) {
      enqueueSnackbar("Could not refresh insurance policies.", {
        variant: "error",
      });
      return null;
    }
  };

  const rebookInsurance = async ({
    booking,
    payload: customPayload,
    setLoading,
    callback,
  }) => {
    const insuranceBookingId = booking?._id;
    const payload = customPayload || buildInsuranceRebookPayload(booking);

    if (!insuranceBookingId) {
      enqueueSnackbar("Insurance booking id is missing.", {
        variant: "error",
      });
      return false;
    }

    if (!payload) {
      enqueueSnackbar("Unable to rebook: missing booking request data", {
        variant: "error",
      });
      return false;
    }

    let success = false;
    setLoading && setLoading(true);
    await fetchServer({
      method: "POST",
      url: `/product/v1/insurance/rebook/${insuranceBookingId}`,
      data: payload,
    })
      .then((res) => {
        if (res) {
          if (res.status === 200) {
            const bookingData = res?.data?.data?.data || res?.data?.data;
            enqueueSnackbar("Insurance rebooked successfully", {
              variant: "success",
            });
            success = true;
            callback && callback(bookingData);
          } else {
            enqueueSnackbar(
              res?.data?.message ||
                res?.data?.error ||
                res?.message ||
                "Insurance rebook failed.",
              {
                variant: "error",
              },
            );
          }
        }
      })
      .catch(() => {
        enqueueSnackbar("Insurance rebook failed: Network error", {
          variant: "error",
        });
      });
    setLoading && setLoading(false);
    return success;
  };

  return {
    getFlightPolicies,
    searchInsurancePolicies,
    bookInsurance,
    rebookInsurance,
    initializeInsurancePayment,
    getInsuranceBooking,
    cancelAndRefundInsurance,
    getInsuranceBookings,
    getFlightOrderForInsurance,
    cancelPendingInsuranceOrder,
    updateFarePrice,
    confirmInsuranceOrder,
    declineAwaitingAdminInsuranceOrder,
  };
}
